import { computed, nextTick, onBeforeUnmount, onMounted, ref } from 'vue'

const inset = 8
const clamp = (value, max) => Math.max(inset, Math.min(value, Math.max(inset, max)))

// Positions belong to the floating element, independent of the surrounding page layout.
export function useFloatingDrag(element) {
  const position = ref(null)
  const dragging = ref(false)
  let gesture
  let suppressClickUntil = 0
  const style = computed(() => position.value ? {
    left: `${position.value.x}px`, top: `${position.value.y}px`, right: 'auto', bottom: 'auto',
  } : {})
  function move(x, y) {
    const bounds = element.value?.getBoundingClientRect()
    if (!bounds?.width || !bounds.height) return
    position.value = {
      x: clamp(x, window.innerWidth - bounds.width - inset),
      y: clamp(y, window.innerHeight - bounds.height - inset),
    }
  }
  function keepInViewport() {
    if (position.value) move(position.value.x, position.value.y)
  }
  function start(event) {
    if (event.button !== 0 || event.isPrimary === false) return
    const bounds = element.value?.getBoundingClientRect()
    if (!bounds) return
    gesture = { id: event.pointerId, x: event.clientX, y: event.clientY, left: bounds.left, top: bounds.top, target: event.currentTarget }
    event.currentTarget.setPointerCapture?.(event.pointerId)
  }
  function update(event) {
    if (!gesture || event.pointerId !== gesture.id) return
    const dx = event.clientX - gesture.x
    const dy = event.clientY - gesture.y
    if (!dragging.value && Math.hypot(dx, dy) < 6) return
    dragging.value = true
    event.preventDefault()
    move(gesture.left + dx, gesture.top + dy)
  }
  function end(event) {
    if (!gesture || (event && event.pointerId !== gesture.id)) return
    if (dragging.value) suppressClickUntil = Date.now() + 300
    const { target, id } = gesture
    gesture = undefined
    dragging.value = false
    if (target.hasPointerCapture?.(id)) target.releasePointerCapture(id)
  }
  function wasDrag(event) {
    return event?.detail > 0 && Date.now() < suppressClickUntil
  }
  function onKeydown(event) {
    const direction = { ArrowLeft: [-1, 0], ArrowRight: [1, 0], ArrowUp: [0, -1], ArrowDown: [0, 1] }[event.key]
    if (!direction) return
    const bounds = element.value?.getBoundingClientRect()
    if (!bounds) return
    event.preventDefault()
    const distance = event.shiftKey ? 40 : 10
    move(bounds.left + direction[0] * distance, bounds.top + direction[1] * distance)
  }
  onMounted(() => window.addEventListener('resize', keepInViewport))
  onBeforeUnmount(() => { end(); window.removeEventListener('resize', keepInViewport) })
  return { style, dragging, start, update, end, wasDrag, onKeydown, refresh: () => nextTick(keepInViewport) }
}
